import { useState } from 'react'
import {
  ClipboardDocumentIcon,
  CheckIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline'

const TABS = ['HTML', 'WordPress', 'Shopify']

const TAB_INSTRUCTIONS = {
  HTML: 'Paste this code snippet just before the closing </body> tag of your HTML page.',
  WordPress:
    'Go to Appearance > Theme Editor > footer.php (or use a plugin like "Insert Headers and Footers") and paste this code before the closing </body> tag.',
  Shopify:
    'Go to Online Store > Themes > Edit Code > theme.liquid and paste this code just before the closing </body> tag.',
}

export default function Step5Install({ apiKey, onComplete }) {
  const [activeTab, setActiveTab] = useState('HTML')
  const [copied, setCopied] = useState(false)

  const embedCode = `<script src="${window.location.origin}/embed.js" data-api-key="${apiKey}"></script>`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = embedCode
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="py-2">
      <h2 className="text-xl font-bold text-slate-900 mb-1">Add to your website</h2>
      <p className="text-slate-500 mb-6">
        Copy the embed code below and add it to your website to activate your AI assistant.
      </p>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-4">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Instructions */}
      <div className="flex items-start gap-2 mb-3">
        <CodeBracketIcon className="h-5 w-5 text-slate-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-slate-600">{TAB_INSTRUCTIONS[activeTab]}</p>
      </div>

      {/* Code block */}
      <div className="relative mb-6">
        <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl text-sm overflow-x-auto font-mono leading-relaxed">
          <code>{embedCode}</code>
        </pre>
        <button
          onClick={handleCopy}
          className={`absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            copied
              ? 'bg-green-500 text-white'
              : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
          }`}
        >
          {copied ? (
            <>
              <CheckIcon className="h-3.5 w-3.5" />
              Copied!
            </>
          ) : (
            <>
              <ClipboardDocumentIcon className="h-3.5 w-3.5" />
              Copy
            </>
          )}
        </button>
      </div>

      {/* Go to Dashboard button */}
      <button
        onClick={onComplete}
        className="w-full px-8 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
      >
        Go to Dashboard
      </button>
    </div>
  )
}
